import type { LibraryItemView } from '@/lib/api/generated/model';
import { hasUpdate, isInWorkspace } from './presence';
import type { LibraryStateFilter, LibraryTab } from './searchParams';

export interface FilteredItems {
  visible: LibraryItemView[];
  /** Per kind, after the search filter, so tab counts answer "how many match". */
  counts: Record<LibraryTab, number>;
}

function withinNarrowing(
  item: LibraryItemView,
  narrowing: LibraryStateFilter,
): boolean {
  if (narrowing === 'installed') return isInWorkspace(item.state);
  if (narrowing === 'updates') return hasUpdate(item.state);
  // What members shared, beside what the curators put in (spec 206).
  if (narrowing === 'shared') return item.origin === 'shared';
  return true;
}

/** The shortest search that may be read as naming a kind rather than a word. */
const KIND_PREFIX_MIN = 3;

function matches(
  item: LibraryItemView,
  needle: string,
  names: ReadonlyMap<string, string>,
): boolean {
  if (item.name.toLowerCase().includes(needle)) return true;
  if (item.description.toLowerCase().includes(needle)) return true;
  // The key is the folder name: what a prompt names and what appears in Files.
  // A workflow's key and its name are often different words.
  if (item.key.toLowerCase().includes(needle)) return true;
  // What a workflow needs is part of what it is. Searching an agent's key
  // finds the agent and every workflow that delegates to it.
  // By name too, since a linked workflow is known by its title (spec 109
  // FR-012): "feature request" finds every workflow that hands work to it.
  if (
    (item.requires ?? []).some(
      (dep) =>
        dep.key.toLowerCase().includes(needle) ||
        (names.get(`${dep.kind}/${dep.key}`) ?? '').includes(needle),
    )
  )
    return true;
  // "workflow", "agents", "ski" — a kind typed as a word. Three letters at
  // least, so that "a" does not quietly select every agent.
  return needle.length >= KIND_PREFIX_MIN && item.kind.startsWith(needle);
}

/**
 * The current tab's items matching the search and the narrowing, and every
 * tab's count.
 *
 * The search is case-insensitive over a name, a description, a key, the keys
 * and names of what a workflow needs, and the kind as a word. Counts are computed after both
 * the search and the narrowing on purpose (spec 105 FR-005): a reader who
 * narrowed and typed wants to know where the remaining matches are, and a count
 * that ignored either would promise rows its tab will not show.
 */
export function filterItems(
  items: readonly LibraryItemView[],
  tab: LibraryTab,
  q: string,
  narrowing: LibraryStateFilter = 'all',
): FilteredItems {
  const needle = q.trim().toLowerCase();
  const names = new Map(
    items.map((i) => [`${i.kind}/${i.key}`, i.name.toLowerCase()]),
  );
  const matching = items.filter(
    (item) =>
      withinNarrowing(item, narrowing) &&
      (!needle || matches(item, needle, names)),
  );
  const counts: Record<LibraryTab, number> = {
    workflow: 0,
    agent: 0,
    skill: 0,
  };
  for (const item of matching) counts[item.kind] += 1;
  const visible = matching
    .filter((item) => item.kind === tab)
    .sort((a, b) => a.name.localeCompare(b.name));
  return { visible, counts };
}
